import type { StampMeta } from "./StampMeta";
import { newId } from "../model/ids";

/**
 * Validates and imports a user-supplied SVG file as a stamp.
 * - Strips <script>, on* attributes, and external href references.
 * - Computes bbox from the viewBox or measured size.
 */
export async function importStampFromFile(file: File): Promise<StampMeta> {
  if (!/\.svg$/i.test(file.name) && file.type !== "image/svg+xml") {
    throw new Error("Only SVG stamps are supported in v1");
  }
  const text = await file.text();
  return parseStampSvg(text, file.name.replace(/\.svg$/i, ""));
}

export function parseStampSvg(svgText: string, name: string): StampMeta {
  const doc = new DOMParser().parseFromString(svgText, "image/svg+xml");
  const root = doc.querySelector("svg");
  if (!root) throw new Error("Invalid SVG: no <svg> root");

  // Sanitize.
  for (const script of doc.querySelectorAll("script")) script.remove();
  for (const el of doc.querySelectorAll("*")) {
    for (const attr of [...el.attributes]) {
      if (attr.name.startsWith("on")) el.removeAttribute(attr.name);
      if (
        (attr.name === "href" || attr.name === "xlink:href") &&
        /^\s*(javascript|data:text\/html)/i.test(attr.value)
      ) {
        el.removeAttribute(attr.name);
      }
    }
  }

  // Determine bbox.
  let bbox = { x: 0, y: 0, w: 24, h: 24 };
  const vb = root.getAttribute("viewBox");
  if (vb) {
    const parts = vb.split(/[\s,]+/).map(Number);
    if (parts.length === 4 && parts.every((n) => Number.isFinite(n))) {
      bbox = {
        x: parts[0]!,
        y: parts[1]!,
        w: Math.max(0.001, parts[2]!),
        h: Math.max(0.001, parts[3]!),
      };
    }
  } else {
    const w = parseFloat(root.getAttribute("width") || "24") || 24;
    const h = parseFloat(root.getAttribute("height") || "24") || 24;
    bbox = { x: 0, y: 0, w, h };
    root.setAttribute("viewBox", `0 0 ${w} ${h}`);
  }

  const cleanedSvg = new XMLSerializer().serializeToString(doc);

  return {
    id: newId("ustamp"),
    name,
    category: "figure",
    tags: ["user"],
    svg: cleanedSvg,
    bbox,
    deboss: true,
  };
}
